import type { RentalAgreementDto } from '../../services/api';
import { i18nService, t } from '../../services/i18n';
import { escapeHtml, formatDate, isAgreementActive } from './property-shared';

function getAgreementStartTimestamp(agreement: RentalAgreementDto): number {
  if (!agreement.startDate) return Number.POSITIVE_INFINITY;
  const timestamp = new Date(agreement.startDate).getTime();
  return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
}

function renderHistoryEntry(agreement: RentalAgreementDto, isLatest: boolean): string {
  const amount = agreement.rentAmount != null
    ? `${i18nService.formatCurrency(agreement.rentAmount)}${t('property.perMonth')}`
    : '-';
  const endLabel = agreement.endDate ? formatDate(agreement.endDate) : t('property.rentHistory.ongoing');

  return `
    <article class="rent-history-entry">
      <div class="rent-history-entry-line" aria-hidden="true"></div>
      <div class="rent-history-entry-dot" aria-hidden="true"></div>
      <div class="rent-history-entry-date">
        <span class="rent-history-entry-date-label">${t('common.startDate')}</span>
        <strong>${formatDate(agreement.startDate)}</strong>
      </div>
      <div class="rent-history-entry-card">
        <div class="rent-history-entry-header">
          <div>
            <div class="rent-history-entry-price">${amount}</div>
            <div class="rent-history-entry-meta">${escapeHtml(agreement.tenantName)}</div>
          </div>
          <div class="rent-history-entry-badges">
            ${isLatest ? `<span class="badge badge-info">${t('property.rentHistory.latestRent')}</span>` : ''}
            <span class="badge ${isAgreementActive(agreement) ? 'badge-success' : 'badge-muted'}">${t(isAgreementActive(agreement) ? 'common.active' : 'common.ended')}</span>
          </div>
        </div>
        <div class="rent-history-entry-period">
          <span>${t('common.startDate')}: ${formatDate(agreement.startDate)}</span>
          <span>${t('common.endDate')}: ${endLabel}</span>
        </div>
      </div>
    </article>
  `;
}

export class RcPropertyRentHistoryTab extends HTMLElement {
  agreements: RentalAgreementDto[] = [];

  render(): void {
    const history = this.agreements
      .filter((agreement) => Boolean(agreement.startDate))
      .sort((left, right) => getAgreementStartTimestamp(left) - getAgreementStartTimestamp(right));

    const latestAgreement = [...history]
      .reverse()
      .find((agreement) => agreement.rentAmount != null);

    this.innerHTML = `
      <section class="rent-history-card">
        <div class="rent-history-header">
          <div>
            <h3>${t('property.rentHistory.title')}</h3>
            <p>${t('property.rentHistory.subtitle')}</p>
          </div>
          <div class="rent-history-summary">
            <span class="badge badge-info">${history.length} ${t('property.rentHistory.records')}</span>
            ${latestAgreement?.rentAmount != null ? `<span class="badge badge-success">${t('property.rentHistory.latestRent')}: ${i18nService.formatCurrency(latestAgreement.rentAmount)}</span>` : ''}
          </div>
        </div>
        ${history.length === 0
          ? `<div class="empty-state rent-history-empty"><p>${t('property.rentHistory.empty')}</p></div>`
          : `<div class="rent-history-timeline">${history.map((agreement) => renderHistoryEntry(agreement, latestAgreement?.id === agreement.id)).join('')}</div>`}
      </section>
    `;
  }
}

if (!customElements.get('rc-property-rent-history-tab')) {
  customElements.define('rc-property-rent-history-tab', RcPropertyRentHistoryTab);
}
